import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ErrorMessage from '../components/ErrorMessage'

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.bumpkinlabs.recall'
const APP_STORE_URL = 'https://apps.apple.com/app/id6766313661'

type Mode = 'signin' | 'signup'

export default function Login() {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [confirmationSent, setConfirmationSent] = useState(false)
  const { signInWithEmail, signUpWithEmail, signInWithGoogle, error, clearError, session } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/today'

  useEffect(() => {
    if (session) navigate(from, { replace: true, state: { postAuth: true } })
  }, [session, navigate, from])

  function toggleMode() {
    setMode(m => (m === 'signin' ? 'signup' : 'signin'))
    setConfirmationSent(false)
    clearError()
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    clearError()
    if (mode === 'signup') {
      const result = await signUpWithEmail(email, password)
      if (result.needsConfirmation) setConfirmationSent(true)
    } else {
      await signInWithEmail(email, password)
    }
    setSubmitting(false)
  }

  async function handleGoogle() {
    clearError()
    await signInWithGoogle()
  }

  if (confirmationSent) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card elevation={2} sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" color="primary" sx={{ mb: 0.5, fontWeight: 700, textAlign: 'center' }}>
              Recall
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
              Check your inbox
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              We sent a confirmation link to {email}. Open it to finish creating your account.
            </Typography>
            <Button variant="outlined" fullWidth onClick={() => { setConfirmationSent(false); setMode('signin') }}>
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card elevation={2} sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" color="primary" sx={{ mb: 0.5, fontWeight: 700, textAlign: 'center' }}>
            Recall
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
            {mode === 'signup' ? 'Create your account' : 'Sign in'}
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                required
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
                fullWidth
              />
              {error && <ErrorMessage message={error} />}
              <Button variant="contained" type="submit" fullWidth size="large" disabled={submitting}>
                {/* Must be a <span>, not a bare text node: Chrome's translator wraps text nodes in <font> and React then holds a stale reference. */}
                {submitting ? <CircularProgress size={22} color="inherit" /> : <span>{mode === 'signup' ? 'Create account' : 'Sign in'}</span>}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Button variant="text" size="small" onClick={toggleMode}>
              {mode === 'signup' ? 'Already have an account? Sign in' : "New to Recall? Create an account"}
            </Button>
          </Box>

          {mode === 'signin' && (
            <Box sx={{ textAlign: 'center' }}>
              <Button variant="text" size="small" onClick={() => navigate('/forgot-password')}>
                Forgot password?
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">or</Typography></Divider>

          <Button variant="outlined" onClick={handleGoogle} fullWidth sx={{ mb: 3 }}>
            Continue with Google
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Premium subscriptions are available in the{' '}
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              iOS
              <span className="sr-only"> app (opens in a new tab)</span>
            </a>{' '}
            and{' '}
            <a href={PLAY_URL} target="_blank" rel="noopener noreferrer">
              Android
              <span className="sr-only"> app (opens in a new tab)</span>
            </a>{' '}
            app.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}
